import { useEffect, useState } from 'react';
import { Search, ChevronDown, LogOut, Menu, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePersona } from '@/contexts/PersonaContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { NotificationDropdown } from './NotificationDropdown';
import { MobileSidebarNav } from './MobileSidebarNav';
import { SearchCommand } from './SearchCommand';
import { hasApprovedKyc } from '@/lib/access';
import { BrandLockup } from '@/components/brand/BrandLogo';
import { supabase } from '@/integrations/supabase/client';

function getAvatarPath(value?: string | null) {
  if (!value) return null;

  try {
    const url = new URL(value);
    const publicMarker = "/object/public/avatars/";
    const signedMarker = "/object/sign/avatars/";
    const marker = url.pathname.includes(publicMarker) ? publicMarker : signedMarker;

    if (url.pathname.includes(marker)) {
      return decodeURIComponent(url.pathname.split(marker)[1] || "").split("?")[0] || null;
    }

    return value;
  } catch {
    return value;
  }
}

export function TopNav() {
  const { profile, signOut, clientDashboardMode, updateClientDashboardMode } = useAuth();
  const { personaLabel, personaColor } = usePersona();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [displayAvatarUrl, setDisplayAvatarUrl] = useState<string | null>(null);

  const initials = profile
    ? `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase()
    : '??';
  const fullName = profile ? `${profile.first_name} ${profile.last_name}` : 'User';
  const isVerified = hasApprovedKyc(profile) || Boolean(profile?.is_verified);

  useEffect(() => {
    let isMounted = true;
    const avatarPath = getAvatarPath(profile?.avatar_url);

    if (!avatarPath) {
      setDisplayAvatarUrl(null);
      return;
    }

    if (/^https?:\/\//i.test(avatarPath)) {
      setDisplayAvatarUrl(avatarPath);
      return;
    }

    supabase.storage
      .from("avatars")
      .createSignedUrl(avatarPath, 60 * 60)
      .then(({ data, error }) => {
        if (!isMounted) return;
        setDisplayAvatarUrl(error ? null : data?.signedUrl || null);
      });

    return () => {
      isMounted = false;
    };
  }, [profile?.avatar_url]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleClientModeChange = async (enabled: boolean) => {
    try {
      await updateClientDashboardMode(enabled ? "seller" : "buyer");
    } catch {
      // AuthContext keeps the local setting usable even if metadata persistence is temporarily unavailable.
    }
  };

  return (
    <>
      <header className="h-16 border-b border-border bg-card px-4 md:px-6 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2 md:gap-3">
          {isMobile && (
            <Button variant="ghost" size="icon" onClick={() => setMobileNavOpen(true)} className="shrink-0" aria-label="Open navigation menu">
              <Menu className="w-5 h-5" />
            </Button>
          )}
          <BrandLockup
            showCountry={!isMobile}
            className="gap-2"
            markClassName="h-8 md:h-9"
            textClassName="text-base md:text-lg"
            subtextClassName="hidden sm:block"
          />
        </div>

        {/* Desktop search trigger */}
        <div className="hidden md:flex flex-1 max-w-md mx-8">
          <button
            onClick={() => setSearchOpen(true)}
            aria-label="Search transactions, properties, clients"
            className="w-full h-10 pl-10 pr-4 rounded-lg bg-secondary text-sm text-muted-foreground text-left relative flex items-center hover:bg-secondary/80 transition-colors"
          >
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            Search transactions, properties, clients...
            <kbd className="ml-auto hidden lg:inline-flex h-5 items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
              ⌘K
            </kbd>
          </button>
        </div>

        <div className="flex items-center gap-1 md:gap-3">
          {isMobile && (
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} aria-label="Search">
              <Search className="w-5 h-5" />
            </Button>
          )}
          {profile?.role === "client" ? (
            <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-2 py-1">
              <span className={`text-xs font-medium ${clientDashboardMode === "buyer" ? "text-foreground" : "text-muted-foreground"}`}>
                Buyer
              </span>
              <Switch
                checked={clientDashboardMode === "seller"}
                onCheckedChange={(value) => void handleClientModeChange(value)}
                aria-label="Switch between buyer and seller dashboard"
              />
              <span className={`text-xs font-medium ${clientDashboardMode === "seller" ? "text-foreground" : "text-muted-foreground"}`}>
                Seller
              </span>
            </div>
          ) : (
            <Badge className={`${personaColor} text-xs px-2 hidden sm:inline-flex`}>{personaLabel}</Badge>
          )}
          <NotificationDropdown />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 pl-2 pr-2 md:pr-3">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={displayAvatarUrl || ''} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">{initials}</AvatarFallback>
                </Avatar>
                <div className="hidden sm:flex flex-col items-start">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{fullName}</span>
                    {isVerified ? (
                      <span aria-label="Verified account" className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-white">
                        <Check className="h-3 w-3" />
                      </span>
                    ) : null}
                  </div>
                  <span className="text-xs text-muted-foreground">{personaLabel}</span>
                </div>
                <ChevronDown className="w-3 h-3 text-muted-foreground hidden sm:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>Profile Settings</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/notifications')}>Notifications</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/help')}>Help & Support</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={handleSignOut}>
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <SearchCommand open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Mobile navigation sheet */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent side="left" className="w-72 p-0">
          <SheetHeader className="px-4 py-4 border-b border-border">
            <SheetTitle>
              <BrandLockup markClassName="h-8" textClassName="text-lg" />
            </SheetTitle>
          </SheetHeader>
          <MobileSidebarNav onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  );
}
